import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { ReactComponent as AkiveLogo } from '../../../assets/images/akive_logo.svg'
import { ROUTES } from '../../../constants/routes'
import {
  selectColorMode,
  toggleColorMode,
} from '../../preferences/model/preferencesSlice'

export default function AuthLayout({ children }) {
  const dispatch = useDispatch()
  const mode = useSelector(selectColorMode)

  return (
    <AuthPage>
      <BrandPanel aria-label="akive 소개">
        <BrandInner>
          <BrandLogoLink to="/" aria-label="akive 홈으로 이동">
            <AkiveLogo aria-hidden="true" />
          </BrandLogoLink>
          <BrandBody>
            <JourneyIndex aria-label="목표, 계획, 실행, 기록, 회고로 이어지는 akive의 학습 흐름">
              <li><b>01</b><span><strong>목표</strong><em>방향 세우기</em></span></li>
              <li><b>02</b><span><strong>계획</strong><em>과정 설계</em></span></li>
              <li><b>03</b><span><strong>실행</strong><em>몰입 시작</em></span></li>
              <li><b>04</b><span><strong>기록</strong><em>순간 남기기</em></span></li>
              <li><b>05</b><span><strong>회고</strong><em>성장 발견</em></span></li>
            </JourneyIndex>
            <BrandMessage>
              <BrandEyebrow>LEARNING OPERATING SYSTEM</BrandEyebrow>
              <h1>학습의 모든 순간을 하나로</h1>
              <p>목표부터 계획, 실행, 기록, 회고까지.</p>
              <BrandStatement>학습의 전 과정을 운영하는 <span>Learning OS</span></BrandStatement>
            </BrandMessage>
          </BrandBody>
          <BrandFooter>
            <span>PLAN · EXECUTE · RECORD · REVIEW</span>
            <i aria-hidden="true" />
          </BrandFooter>
        </BrandInner>
      </BrandPanel>

      <FormPanel>
        <ThemeButton
          type="button"
          role="switch"
          $dark={mode === 'dark'}
          aria-checked={mode === 'dark'}
          aria-label={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          onClick={() => dispatch(toggleColorMode())}
        >
          <ThemeModeIcon $active={mode === 'light'}>
            <LightModeRoundedIcon aria-hidden="true" />
          </ThemeModeIcon>
          <ThemeModeIcon $active={mode === 'dark'}>
            <DarkModeRoundedIcon aria-hidden="true" />
          </ThemeModeIcon>
        </ThemeButton>

        <FormColumn>
          <MobileLogoLink to="/" aria-label="akive 홈으로 이동">
            <AkiveLogo aria-hidden="true" />
          </MobileLogoLink>
          {children}
          <PolicyLinks>
            <Link to={ROUTES.TERMS_OF_SERVICE}>서비스 이용약관</Link>
            <span aria-hidden="true">·</span>
            <Link to={ROUTES.PRIVACY_POLICY}>개인정보 처리방침</Link>
            <span aria-hidden="true">·</span>
            <Link to={ROUTES.OPEN_SOURCE_LICENSES}>오픈소스 라이선스</Link>
          </PolicyLinks>
        </FormColumn>
      </FormPanel>
    </AuthPage>
  )
}

const AuthPage = styled.main`
  display: grid;
  min-width: 320px;
  min-height: 100dvh;
  grid-template-columns: minmax(400px, 1fr) minmax(460px, 1fr);
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: 860px) { display: block; }
`

const BrandPanel = styled.aside`
  --brand-accent: #D94B6A;
  position: relative;
  min-height: 100dvh;
  overflow: hidden;
  color: #FFFFFF;
  background: #111B40;

  @media (max-width: 860px) { display: none; }
`

const BrandInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  min-height: 100%;
  flex-direction: column;
  padding: clamp(38px, 4vw, 58px) clamp(32px, 4vw, 54px);
`

const BrandLogoLink = styled(Link)`
  display: inline-flex;
  align-self: flex-start;
  color: #FFFFFF;

  svg { display: block; width: 116px; height: auto; }
`

const BrandBody = styled.div`
  display: grid;
  min-height: 0;
  flex: 1;
  grid-template-columns: 132px minmax(0, 1fr);
  align-items: center;
  gap: clamp(40px, 5vw, 72px);
  padding: 28px 0 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 96px minmax(0, 1fr);
    gap: 28px;
  }
`

const BrandMessage = styled.div`
  min-width: 0;
  padding-left: clamp(28px, 3vw, 44px);
  border-left: 1px solid rgba(255, 255, 255, 0.13);

  h1 {
    margin: 0;
    font-size: clamp(2.05rem, 3.2vw, 3rem);
    line-height: 1.25;
    letter-spacing: -0.045em;
  }

  p {
    max-width: 420px;
    margin: 18px 0 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
    line-height: 1.75;
    white-space: nowrap;
  }
`

const BrandEyebrow = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  color: var(--brand-accent);
  font-size: 0.73rem;
  font-weight: 760;
  letter-spacing: 0.13em;

  &::before {
    width: 24px;
    height: 1px;
    background: currentColor;
    content: '';
  }
`

const BrandStatement = styled.small`
  display: block;
  margin-top: 9px;
  color: rgba(255, 255, 255, 0.46);
  font-size: 0.75rem;
  line-height: 1.6;
  white-space: nowrap;

  span { color: #FFFFFF; font-weight: 680; }
`

const JourneyIndex = styled.ol`
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    display: grid;
    min-height: 80px;
    grid-template-columns: 25px minmax(0, 1fr);
    gap: 9px;
    color: rgba(255, 255, 255, 0.52);
  }

  li::before {
    position: absolute;
    top: 5px;
    left: 6px;
    z-index: 1;
    width: 9px;
    height: 9px;
    border: 2px solid #111B40;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.34);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
    content: '';
  }

  li::after {
    position: absolute;
    top: 15px;
    bottom: 0;
    left: 10px;
    width: 1px;
    background: rgba(255, 255, 255, 0.14);
    content: '';
  }

  li:nth-child(-n + 3)::before {
    background: var(--brand-accent);
  }

  li:nth-child(-n + 2)::after {
    background: var(--brand-accent);
  }

  li:nth-child(3)::before {
    box-shadow: 0 0 0 4px rgba(217, 75, 106, 0.18);
  }

  li:last-child::after { display: none; }

  b {
    padding-top: 25px;
    color: rgba(255, 255, 255, 0.3);
    font-size: 0.56rem;
    font-weight: 680;
  }

  span { display: grid; align-content: start; gap: 1px; }
  strong { color: #FFFFFF; font-size: 0.82rem; font-weight: 680; }
  em { font-size: 0.67rem; font-style: normal; white-space: nowrap; }

  @media (max-width: 1100px) {
    li { min-height: 67px; grid-template-columns: 22px minmax(0, 1fr); gap: 6px; }
    em { display: none; }
  }
`

const BrandFooter = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 18px;
  color: rgba(255, 255, 255, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.62rem;
  font-weight: 620;
  letter-spacing: 0.08em;

  i {
    width: 46px;
    height: 3px;
    flex: 0 0 auto;
    background: var(--brand-accent);
  }
`

const FormPanel = styled.section`
  position: relative;
  display: grid;
  min-height: 100dvh;
  place-items: center;
  padding: 76px clamp(36px, 8vw, 112px) 42px;

  @media (max-width: 860px) { padding: 78px 22px 32px; }
  @media (max-width: 480px) { align-items: start; padding: 68px 18px 24px; }
`

const ThemeButton = styled.button`
  position: absolute;
  top: 24px;
  right: 28px;
  display: inline-flex;
  width: 64px;
  height: 32px;
  align-items: center;
  justify-content: space-between;
  padding: 0 3px;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};
  transition: border-color 150ms ease, background 150ms ease;

  &::before {
    position: absolute;
    z-index: 0;
    top: 2px;
    left: 2px;
    width: 26px;
    height: 26px;
    content: '';
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: ${({ theme }) => theme.shadow.low};
    transform: translateX(${({ $dark }) => $dark ? '32px' : '0'});
    transition: transform 180ms ease, background 150ms ease;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryAccent};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    top: 16px;
    right: 18px;
    width: 58px;
    height: 30px;

    &::before {
      width: 24px;
      height: 24px;
      transform: translateX(${({ $dark }) => $dark ? '28px' : '0'});
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::before { transition: none; }
  }
`

const ThemeModeIcon = styled.span`
  position: relative;
  z-index: 1;
  display: inline-grid;
  width: 28px;
  height: 28px;
  place-items: center;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textMuted};
  opacity: ${({ $active }) => $active ? 1 : 0.58};
  transition: color 150ms ease, opacity 150ms ease;

  svg { width: 16px; height: 16px; }

  @media (max-width: 480px) {
    width: 25px;
    height: 25px;
    svg { width: 15px; height: 15px; }
  }
`

const FormColumn = styled.div`
  width: min(100%, 430px);
`

const MobileLogoLink = styled(Link)`
  display: none;
  width: fit-content;
  margin-bottom: 42px;
  color: ${({ theme }) => theme.colors.primary};

  svg { display: block; width: 102px; height: auto; }

  @media (max-width: 860px) { display: inline-flex; }
  @media (max-width: 480px) { margin-bottom: 34px; }
`

const PolicyLinks = styled.footer`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 42px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.72rem;

  a:hover { color: ${({ theme }) => theme.colors.text}; }
`
